import { createId } from '../utils/ids.js';
import { MeasurementManager } from './MeasurementManager.js';
import { SnapManager } from './SnapManager.js';

const TOOLS = new Set(['select', 'wall', 'door', 'window', 'measure']);

function floorBaseY(floor) {
  return floor.baseY ?? floor.level * floor.height;
}

export class PlanEditor {
  constructor({ root, renderer, state, history, selection, onStatus = () => {} }) {
    this.root = root;
    this.renderer = renderer;
    this.state = state;
    this.history = history;
    this.selection = selection;
    this.onStatus = onStatus;
    this.snap = new SnapManager();
    this.tool = 'select';
    this.active = false;
    this.start = null;
    this.lastPoint = null;
    this.overlay = root.querySelector('[data-plan-input-layer]');
    this.readout = root.querySelector('[data-plan-readout]');
    this.instruction = root.querySelector('[data-plan-instruction]');
    this.activeFloorSelect = root.querySelector('[data-active-floor]');
    this.cleanup = [];
    this.#bind();
  }

  setActive(active) {
    this.active = Boolean(active);
    this.root.classList.toggle('plan-mode', this.active);
    this.#updateInputLayer();
    if (!this.active) this.cancel();
    else this.sync();
  }

  setTool(tool) {
    if (!TOOLS.has(tool)) return;
    this.tool = tool;
    this.cancel();
    this.root.querySelectorAll('[data-plan-tool]').forEach((button) => {
      button.classList.toggle('is-active', button.dataset.planTool === tool);
      button.setAttribute('aria-pressed', String(button.dataset.planTool === tool));
    });
    this.#updateInputLayer();
    const messages = {
      select: 'Select walls, floors, or objects on the plan.',
      wall: 'Tap the wall start point, then tap its end point.',
      door: 'Tap near a wall to place a door.',
      window: 'Tap near a wall to place a window.',
      measure: 'Tap two points to add a saved measurement.'
    };
    this.#setInstruction(messages[tool]);
  }

  sync() {
    const floor = this.getActiveFloor();
    if (!floor) return;
    this.renderer.setPlanFloor(floor.id);
    this.renderer.setPlanMeasurements(floor.measurements ?? [], floorBaseY(floor) + 0.09);
    const floorLabel = this.root.querySelector('[data-plan-floor-label]');
    if (floorLabel) floorLabel.textContent = floor.name;
  }

  getActiveFloor() {
    const selectedId = this.activeFloorSelect?.value;
    return this.state.data.floors.find((floor) => floor.id === selectedId) ?? this.state.data.floors[0] ?? null;
  }

  snapCoordinates(point, reference = null) {
    const floor = this.getActiveFloor();
    const endpoints = (floor?.walls ?? []).flatMap((wall) => [wall.start, wall.end]);
    return this.snap.snapPoint(point, { reference, endpoints }).point;
  }

  cancel() {
    this.start = null;
    this.lastPoint = null;
    this.renderer.clearPlanGuide();
    this.#setReadout('—');
  }

  dispose() {
    this.cancel();
    for (const callback of this.cleanup.splice(0)) callback();
  }

  #bind() {
    for (const button of this.root.querySelectorAll('[data-plan-tool]')) {
      const listener = () => this.setTool(button.dataset.planTool);
      button.addEventListener('click', listener);
      this.cleanup.push(() => button.removeEventListener('click', listener));
    }

    const snapInput = this.root.querySelector('[data-plan-snap]');
    const onSnap = () => this.snap.setEnabled(snapInput.checked);
    snapInput?.addEventListener('change', onSnap);
    if (snapInput) this.cleanup.push(() => snapInput.removeEventListener('change', onSnap));

    const gridInput = this.root.querySelector('[data-plan-grid]');
    const onGrid = () => this.renderer.setGridVisible(gridInput.checked);
    gridInput?.addEventListener('change', onGrid);
    if (gridInput) this.cleanup.push(() => gridInput.removeEventListener('change', onGrid));

    const onFloor = () => {
      this.cancel();
      this.renderer.clearSelection();
      this.sync();
    };
    this.activeFloorSelect?.addEventListener('change', onFloor);
    if (this.activeFloorSelect) this.cleanup.push(() => this.activeFloorSelect.removeEventListener('change', onFloor));

    const onMove = (event) => this.#pointerMove(event);
    const onClick = (event) => this.#click(event);
    this.overlay?.addEventListener('pointermove', onMove);
    this.overlay?.addEventListener('click', onClick);
    if (this.overlay) {
      this.cleanup.push(() => this.overlay.removeEventListener('pointermove', onMove));
      this.cleanup.push(() => this.overlay.removeEventListener('click', onClick));
    }

    const onKeyDown = (event) => {
      if (event.code === 'Escape' && this.active && this.start) {
        event.preventDefault();
        this.cancel();
        this.#setInstruction('Drawing cancelled. Choose a new start point.');
      }
    };
    window.addEventListener('keydown', onKeyDown);
    this.cleanup.push(() => window.removeEventListener('keydown', onKeyDown));

    const onStateChange = () => this.sync();
    this.state.addEventListener('change', onStateChange);
    this.cleanup.push(() => this.state.removeEventListener('change', onStateChange));
  }

  #updateInputLayer() {
    this.overlay?.classList.toggle('is-drawing', this.active && this.tool !== 'select');
  }

  #pointFromEvent(event, reference = null) {
    const floor = this.getActiveFloor();
    if (!floor) return null;
    const world = this.#worldPoint(event, floor);
    if (!world) return null;
    return this.snapCoordinates([world.x, world.z], reference);
  }

  #worldPoint(event, floor = this.getActiveFloor()) {
    if (!floor) return null;
    return this.renderer.screenToPlanPoint(event.clientX, event.clientY, floorBaseY(floor) + 0.09);
  }

  #pointerMove(event) {
    if (!this.active || this.tool === 'select') return;
    const floor = this.getActiveFloor();
    if (!floor) return;
    if (this.tool === 'door' || this.tool === 'window') {
      const world = this.#worldPoint(event, floor);
      const projection = world && this.snap.projectToWall([world.x, world.z], floor.walls, 0.9);
      if (!projection) {
        this.renderer.clearPlanGuide();
        this.#setReadout('Move closer to a wall');
        return;
      }
      const width = this.tool === 'door' ? 1 : 1.4;
      const halfX = Math.cos(projection.angle) * width / 2;
      const halfZ = Math.sin(projection.angle) * width / 2;
      this.renderer.setPlanGuide(
        [projection.point[0] - halfX, projection.point[1] - halfZ],
        [projection.point[0] + halfX, projection.point[1] + halfZ],
        floorBaseY(floor) + 0.11,
        this.tool
      );
      this.#setReadout(`${this.tool === 'door' ? 'Door' : 'Window'} · wall snap`);
      return;
    }
    if (!this.start) return;
    const point = this.#pointFromEvent(event, this.tool === 'wall' ? this.start : null);
    if (!point) return;
    this.lastPoint = point;
    this.renderer.setPlanGuide(this.start, point, floorBaseY(floor) + 0.11, this.tool);
    this.#setReadout(MeasurementManager.formatDistance(MeasurementManager.distance(this.start, point), this.state.data.units));
  }

  #click(event) {
    if (!this.active || this.tool === 'select') return;
    event.preventDefault();
    const floor = this.getActiveFloor();
    if (floor && (this.tool === 'door' || this.tool === 'window')) {
      const world = this.#worldPoint(event, floor);
      const projection = world && this.snap.projectToWall([world.x, world.z], floor.walls, 0.9);
      if (!projection) return this.onStatus('Tap closer to a wall to place this opening.', 'warning');
      this.#commitOpening(floor, projection);
      this.cancel();
      return;
    }
    const point = this.#pointFromEvent(event, this.tool === 'wall' ? this.start : null);
    if (!floor || !point) return;
    if (!this.start) {
      this.start = point;
      this.lastPoint = point;
      this.renderer.setPlanGuide(point, point, floorBaseY(floor) + 0.11, this.tool);
      this.#setInstruction('Start point set. Choose the end point.');
      return;
    }

    const validation = this.snap.validateWall(this.start, point);
    if (!validation.valid) {
      this.onStatus(validation.reason, 'warning');
      return;
    }
    if (this.tool === 'wall') this.#commitWall(floor, point, validation.length);
    else this.#commitMeasurement(floor, point);
    this.cancel();
  }

  #commitWall(floor, end, length) {
    const wall = {
      id: createId('wall'),
      name: `Wall ${floor.walls.length + 1}`,
      type: 'wall',
      start: [...this.start],
      end: [...end],
      height: Math.min(3, floor.height - 0.15),
      thickness: 0.16,
      material: 'wall-warm',
      openings: [],
      baseY: floorBaseY(floor),
      floorId: floor.id,
      roomId: floor.rooms[0]?.id ?? null,
      visible: true,
      locked: false,
      userCreated: true
    };
    this.history.execute({
      description: 'Add wall',
      do: () => this.state.addWall(wall, floor.id),
      undo: () => this.state.removeEntity(wall.id)
    });
    this.renderer.selectById(wall.id);
    this.onStatus(`Wall added · ${MeasurementManager.formatDistance(length, this.state.data.units)}`, 'success');
  }

  #commitMeasurement(floor, end) {
    const measurement = MeasurementManager.create(this.start, end, floor.id, this.state.data.units);
    this.history.execute({
      description: 'Add measurement',
      do: () => this.state.addMeasurement(measurement, floor.id),
      undo: () => this.state.removeEntity(measurement.id)
    });
    this.onStatus(`${measurement.name} added`, 'success');
  }

  #commitOpening(floor, projection) {
    const wall = floor.walls.find((item) => item.id === projection.wallId);
    if (!wall) return;
    const isDoor = this.tool === 'door';
    const width = isDoor ? 1 : 1.4;
    const height = isDoor ? 2.2 : 1.2;
    const wallLength = Math.hypot(wall.end[0] - wall.start[0], wall.end[1] - wall.start[1]);
    const safeMargin = Math.min(0.45, width / Math.max(wallLength, 0.01));
    if (projection.t < safeMargin || projection.t > 1 - safeMargin) {
      this.onStatus('Opening is too close to the end of the wall.', 'warning');
      return;
    }
    const key = isDoor ? 'doors' : 'windows';
    const sillHeight = isDoor ? 0 : 0.9;
    const entity = {
      id: createId(isDoor ? 'door' : 'window'),
      name: `${isDoor ? 'Door' : 'Window'} ${floor[key].length + 1}`,
      type: isDoor ? 'door' : 'window',
      position: [projection.point[0], floorBaseY(floor) + sillHeight + (isDoor ? 0 : height / 2), projection.point[1]],
      rotation: [0, -projection.angle, 0],
      scale: [1, 1, 1],
      width,
      height,
      sillHeight,
      material: isDoor ? 'floor-oak' : 'glass-clear',
      swingDirection: isDoor ? 'inward' : undefined,
      floorId: floor.id,
      roomId: floor.rooms[0]?.id ?? null,
      wallId: wall.id,
      wallPosition: projection.t,
      visible: true,
      locked: false,
      userCreated: true
    };
    this.history.execute({
      description: `Add ${entity.type}`,
      do: () => this.state.insertEntity(floor.id, key, entity),
      undo: () => this.state.removeEntity(entity.id)
    });
    this.renderer.selectById(entity.id);
    this.onStatus(`${entity.name} added to ${wall.name}.`, 'success');
  }

  #setReadout(value) {
    if (this.readout) this.readout.textContent = value;
  }

  #setInstruction(value) {
    if (this.instruction) this.instruction.textContent = value;
  }
}
